import GIAbstractManager from 'src/lib/geo_item/GIAbstractManager';
import movieApi from 'src/apis/movie';
import analyticsApi from 'src/apis/analytics';
import type {
  MovieSearchByAreaParams,
  MovieSearchByRoutesParams,
  MovieSearchParams,
  RouteSearchRouteEntry,
} from 'src/models/apis/movie/movieRequest';
import type { AnalyticsLogMovieSearchParams } from 'src/models/apis/analytics/analyticsRequest';
import { dtFormat, ensureDate } from 'src/lib/dateHelper';
import type {
  TopMovieSearchParams,
} from 'src/models';
import type { GIKp, GIMovieList } from 'src/models/geoItem';
import type { RoadNameDisp, MastersContext } from 'src/models/master';
import type { MovieList } from 'src/models/movie';
import { convMovies } from 'src/lib/moviePlayerHelper';

interface InitParams {
  masters: MastersContext;
}

type SearchTypes = 'area' | 'route' | 'detailParam' | 'id';

export default class GIMovieManager extends GIAbstractManager {
  private masters: {
    roadNameDispMap: Record<string, RoadNameDisp>;
    kpMapByKpUid: Map<string, GIKp>;
  };

  constructor({ masters }: InitParams) {
    super();
    this.masters = {
      roadNameDispMap: masters.kp.roadNameDispMap,
      kpMapByKpUid: masters.kp.kpMapByKpUid,
    };
  }

  async getResourceById(id: number): Promise<GIMovieList | null> {
    try {
      const movieLists = await movieApi.searchByMovieId(id);
      const results = this.convMovieSearchResults_(movieLists, 'id');
      return results.length > 0 ? results[0] : null;
    } catch (e) {
      return null;
    }
  }

  getDateReqParams_(params: TopMovieSearchParams): { tsFrom: Date; tsTo: Date } {
    const tsFrom = params.dtFrom;
    const tsTo = new Date(params.dtTo.valueOf() + 86400 * 1000);
    return { tsFrom, tsTo };
  }

  async logMovieSearch(searchType: string, resultCount: number, resTimeMsec: number): Promise<void> {
    try {
      const params: AnalyticsLogMovieSearchParams = {
        search_type: searchType,
        result_count: resultCount,
        res_time_msec: resTimeMsec,
      };
      await analyticsApi.logMovieSearch(params);
    } catch (e) {
      console.warn('logMovieSearch failed');
      console.warn(e);
    }
  }

  /* 詳細パラメータ指定による検索 */
  async getResourcesByParams(params: TopMovieSearchParams): Promise<GIMovieList[]> {
    // 日付
    const { tsFrom, tsTo } = this.getDateReqParams_(params);
    const reqParams: MovieSearchParams = {
      ts_from: tsFrom,
      ts_to: tsTo,
    };
    reqParams.ts_from = tsFrom;
    reqParams.ts_to = tsTo;
    // 路線名, 方向, KP
    if (params.roadNameDispObj) {
      reqParams.road_name_disp = params.roadNameDispObj.roadNameDisp;
      if (params.direction) {
        reqParams.direction = params.direction;
        if (params.kpCalcFrom && params.kpCalcFrom !== '') {
          const kpCalcFrom = parseFloat(params.kpCalcFrom);
          if (!isNaN(kpCalcFrom)) {
            reqParams.kp_calc_from = kpCalcFrom;
          }
        }
        if (params.kpCalcTo && params.kpCalcTo !== '') {
          const kpCalcTo = parseFloat(params.kpCalcTo);
          if (!isNaN(kpCalcTo)) {
            reqParams.kp_calc_to = kpCalcTo;
          }
        }
      }
    }
    // 車番
    if (params.carName) {
      reqParams.car_name = params.carName;
    }
    // 動画タグ
    if (params.tags && params.tags.length > 0) {
      reqParams.tag_ids = params.tags.map(e => e.id);
    }

    const searchStartedAt = new Date();
    const movieLists = await movieApi.search(reqParams);
    const searchType = 'detailParam';
    this.logMovieSearch(searchType, movieLists.length, new Date().getTime() - searchStartedAt.getTime());
    return this.convMovieSearchResults_(movieLists, searchType);
  }

  /* ルート検索 */
  async getResourcesByRoutes(params: TopMovieSearchParams, routes: { data: RouteSearchRouteEntry[] }): Promise<GIMovieList[]> {
    // ルート検索の場合、画面のパラメータ使うと範囲が狭すぎる気もする.
    // が、何かしら日付範囲は指定しておいた方がよいだろうから何か指定しておく.
    const { tsTo } = this.getDateReqParams_(params);
    const reqParams: MovieSearchByRoutesParams = {
      q: routes.data,
      ts_to: tsTo,
      ts_from: new Date(tsTo.valueOf() - 86400 * 1000 * 60),
    };

    const searchStartedAt = new Date();
    const routeMovieLists = await movieApi.searchByRoutes(reqParams);
    const searchType = 'route';
    this.logMovieSearch(searchType, routeMovieLists.length, new Date().getTime() - searchStartedAt.getTime());
    const filteredRouteMovieLists = routeMovieLists.slice(0, 10); // 先頭10件とかにしておく
    const movieLists = filteredRouteMovieLists.map<MovieList>(routeMovieList => {
      return {
        ...routeMovieList,
        carName: '',
        deviceId: '',
        minDistance: 0,
      };
    });
    return this.convMovieSearchResults_(movieLists, searchType);
  }

  /* 範囲検索 */
  async getResourcesByArea(params: TopMovieSearchParams, queryArea: MovieSearchByAreaParams): Promise<Array<GIMovieList>> {
    const reqParams: MovieSearchByAreaParams = { ...queryArea };
    const { tsFrom, tsTo } = this.getDateReqParams_(params);
    reqParams.ts_from = tsFrom;
    reqParams.ts_to = tsTo;
    // 動画タグ
    if (params.tags && params.tags.length > 0) {
      reqParams.tag_ids = params.tags.map(e => e.id);
    }

    const searchStartedAt = new Date();
    const movieLists = await movieApi.searchByArea(reqParams);
    const searchType = 'area';
    if (movieLists.length > 0) {
      // 範囲検索の場合は対象道路外をクリックすると普通に0件なので、
      // 検索結果1件以上のデータのみを送るようにしないと集計したあとの数
      // (1検索あたりの件数平均値とか)が少なく出てしまう
      this.logMovieSearch(searchType, movieLists.length, new Date().getTime() - searchStartedAt.getTime());
    }
    return this.convMovieSearchResults_(movieLists, searchType);
  }

  private getMovieListDateLocationDisp_(movieList: GIMovieList, searchType: SearchTypes): string {
    const displayParts = [];
    if (searchType !== 'route') {
      displayParts.push(dtFormat(movieList.startTs, 'yyyy/mm/dd HH:MM:SS') + '~');
    } else {
      displayParts.push(
        '再生時間',
        dtFormat(movieList.endTs, 'HH:MM:SS'),
      );
    }
    displayParts.push(
      movieList.startKp.roadNameDispShort,
      movieList.startKp.direction,
      movieList.startKp.placeName !== 'main_line' ? movieList.startKp.placeName : '',
      movieList.startKp.kp.toFixed(1) + 'km',
      '付近',
    );
    return displayParts.filter(part => !!part).join(' ');
  }

  private convMovieSearchResults_(movieLists: MovieList[], searchType: SearchTypes): GIMovieList[] {
    const results: GIMovieList[] = [];
    const kpMapByKpUid = this.masters.kpMapByKpUid;
    const roadNameDispMap = this.masters.roadNameDispMap;
    const emptyKp: GIKp = {
      angle: 0,
      bikou1: null,
      direction: '',
      kp: 0,
      kp2: 0,
      kpCalc: 0,
      kpDisp: null,
      kpPrefix: '',
      kpSetId: 0,
      kpUid: '',
      location: { lat: 0, lon: 0 },
      placeName: '',
      roadName: '',
      roadNameDisp: '---',
      roadNameDispShort: '---',
      roadSectionType: '',
    };
    movieLists.forEach(movieList => {
      const startKp = movieList.startKpUid ? kpMapByKpUid.get(movieList.startKpUid) : null;
      const endKp = movieList.endKpUid ? kpMapByKpUid.get(movieList.endKpUid) : null;

      const giMovieList: GIMovieList = {
        ...movieList,
        searchType,
        startTs: ensureDate(movieList.startTs),
        endTs: ensureDate(movieList.endTs),
        startKp: startKp
          ? { ...startKp, roadNameDispShort: roadNameDispMap[startKp.roadNameDisp].roadNameDispShort }
          : { ...emptyKp },
        endKp: endKp
          ? { ...endKp, roadNameDispShort: roadNameDispMap[endKp.roadNameDisp].roadNameDispShort }
          : { ...emptyKp },
        dateLocationDisp: '',
        dateLocationCarDisp: '',
        isSelected: false,
        movies: convMovies(movieList.movies, kpMapByKpUid),
      };
      giMovieList.dateLocationDisp = this.getMovieListDateLocationDisp_(giMovieList, searchType);
      giMovieList.dateLocationCarDisp = giMovieList.dateLocationDisp + ` (車番:${giMovieList.carName})`;

      results.push(giMovieList);
    });

    return results;
  }

  mergeStateInfo(newResources: GIMovieList[], currentResources: GIMovieList[]): GIMovieList[] {
    const currentResourceMap = currentResources.reduce<Record<string, GIMovieList>>((acc, e) => {
      acc[e.id] = e; return acc;
    }, {});
    newResources.forEach(e => {
      if (currentResourceMap[e.id]) {
        e.isSelected = currentResourceMap[e.id].isSelected;
      }
    });
    return newResources;
  }

  async getResources(): Promise<any> {
    console.warn('GIMovieManager#getResources is noop.');
  }

  async createResource(): Promise<any> {
    console.warn('GIMovieManager#createResource is noop.');
  }

  async updateResource(): Promise<any> {
    console.warn('GIMovieManager#updateResource is noop.');
  }

  async deleteResource(): Promise<any> {
    console.warn('GIMovieManager#deleteResource is noop.');
  }
}
